<template>
  <component :is="wrapperTag" :class="rootClass" v-bind="wrapperProps">
    <div class="premium-upsell__glow" />

    <div class="premium-upsell__content">
      <div class="premium-upsell__badge">
        <v-icon small color="#FFD54F">mdi-crown</v-icon>
        Associado Premium
      </div>

      <h2 v-if="variant === 'hero'" class="premium-upsell__title">
        {{ title }}
      </h2>
      <p v-else class="premium-upsell__title premium-upsell__title--compact">
        {{ title }}
      </p>

      <p class="premium-upsell__subtitle">{{ subtitle }}</p>

      <div v-if="potentialEarnings > 0" class="premium-upsell__potential">
        <span class="premium-upsell__potential-label">Potencial na sua rede agora</span>
        <strong class="premium-upsell__potential-value">
          {{ formatCurrency(potentialEarnings) }}
        </strong>
      </div>

      <div v-if="variant !== 'compact'" class="premium-upsell__chips">
        <v-chip x-small color="rgba(255,255,255,0.2)" text-color="#fff" class="mr-1 mb-1">
          Cashback R$ 29,70
        </v-chip>
        <v-chip x-small color="rgba(255,255,255,0.2)" text-color="#fff" class="mr-1 mb-1">
          5 níveis de comissão
        </v-chip>
        <v-chip x-small color="rgba(255,255,255,0.2)" text-color="#fff" class="mb-1">
          Gestor de Associados
        </v-chip>
      </div>

      <div class="premium-upsell__actions">
        <v-btn
          color="#fff"
          class="premium-upsell__cta"
          :large="variant === 'hero'"
          :small="variant === 'compact'"
          depressed
          @click="$emit('cta')"
        >
          <v-icon left small>mdi-lightning-bolt</v-icon>
          {{ ctaLabel }}
        </v-btn>
        <v-btn
          v-if="variant === 'hero' && showSecondary"
          text
          dark
          small
          class="ml-2"
          @click="$emit('learn-more')"
        >
          Ver como funciona
        </v-btn>
      </div>
    </div>

    <div v-if="variant === 'hero'" class="premium-upsell__price-card">
      <span class="premium-upsell__price-label">Investimento único</span>
      <strong class="premium-upsell__price">R$ 297</strong>
      <span class="premium-upsell__price-foot">sem mensalidade</span>
    </div>
  </component>
</template>

<script lang="ts">
import { formatPremiumCurrency } from "@/config/premiumProgram";
import { defineComponent } from "vue";

export default defineComponent({
  name: "PremiumUpsellBanner",
  props: {
    variant: {
      type: String as () => "hero" | "inline" | "compact",
      default: "hero",
    },
    title: {
      type: String,
      default: "Desbloqueie comissões da sua rede",
    },
    subtitle: {
      type: String,
      default:
        "Sem Premium você monta a rede, mas não recebe comissões quando associados aderem ao plano.",
    },
    potentialEarnings: { type: Number, default: 0 },
    ctaLabel: { type: String, default: "Quero ser Premium" },
    showSecondary: { type: Boolean, default: true },
  },
  emits: ["cta", "learn-more"],
  computed: {
    wrapperTag(): string {
      return this.variant === "compact" ? "div" : "div";
    },
    wrapperProps(): Record<string, unknown> {
      return {};
    },
    rootClass(): Record<string, boolean> {
      return {
        "premium-upsell": true,
        "premium-upsell--hero": this.variant === "hero",
        "premium-upsell--inline": this.variant === "inline",
        "premium-upsell--compact": this.variant === "compact",
      };
    },
  },
  setup() {
    return { formatCurrency: formatPremiumCurrency };
  },
});
</script>

<style scoped lang="scss">
.premium-upsell {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(135deg, #c41e3a 0%, #e5183d 45%, #ff6b35 100%);
}
.premium-upsell__glow {
  position: absolute;
  top: -40%;
  right: -10%;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  pointer-events: none;
}
.premium-upsell--hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  padding: 28px 32px;
  align-items: center;
}
.premium-upsell--inline {
  padding: 20px 24px;
}
.premium-upsell--compact {
  padding: 14px 16px;
}
.premium-upsell__content {
  position: relative;
  z-index: 1;
}
.premium-upsell__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: rgba(0, 0, 0, 0.15);
  padding: 4px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
}
.premium-upsell__title {
  margin: 0 0 8px;
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.2;
}
.premium-upsell__title--compact {
  font-size: 1rem;
  margin-bottom: 4px;
}
.premium-upsell__subtitle {
  margin: 0 0 14px;
  font-size: 0.9rem;
  opacity: 0.92;
  max-width: 520px;
  line-height: 1.45;
}
.premium-upsell--compact .premium-upsell__subtitle {
  font-size: 0.8rem;
  margin-bottom: 10px;
}
.premium-upsell__potential {
  display: inline-flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.18);
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.premium-upsell__potential-label {
  font-size: 0.72rem;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.premium-upsell__potential-value {
  font-size: 1.35rem;
  line-height: 1.2;
}
.premium-upsell__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.premium-upsell__cta {
  color: #e5183d !important;
  font-weight: 700 !important;
}
.premium-upsell__price-card {
  position: relative;
  z-index: 1;
  text-align: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 14px;
  padding: 20px 24px;
  min-width: 140px;
}
.premium-upsell__price-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  opacity: 0.85;
}
.premium-upsell__price {
  display: block;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 6px 0;
}
.premium-upsell__price-foot {
  font-size: 0.75rem;
  opacity: 0.8;
}
@media (max-width: 960px) {
  .premium-upsell--hero {
    grid-template-columns: 1fr;
  }
  .premium-upsell__price-card {
    max-width: 200px;
  }
}
</style>
